import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { formatINR, KYC_DOCUMENT_TYPES, CORRECTABLE_CUSTOMER_FIELDS, NOMINEE_RELATIONSHIPS, PAYMENT_METHODS, nomineeToInput, type CustomerField, type NomineeInput } from '@new-wealth/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useConfirm } from '../components/Confirm.js';
import { ReferredByPicker } from '../components/ReferredByPicker.js';

/** NCDs are issued in whole ₹1,00,000 units (owner spec 2026-07-23). */
const LAKH = 100000;

/** Human label for a stored doc_type; unknown/legacy values show as-is. */
function docLabel(t: string): string {
  return KYC_DOCUMENT_TYPES.find((d) => d.key === t)?.label ?? t;
}

/**
 * Date of birth as `dd-mm-yyyy · NN yrs` — same date style as the payout summary
 * sheet, and the age spelled out because it decides the senior-citizen TDS slab.
 * Returns null (renders as '—') when there is no DOB on record: 106 of 580
 * customers have none, mostly wealth-migrated ones.
 */
/** Completed years today, or null when there is no usable date of birth. */
function ageFromDob(dob: unknown): number | null {
  const m = String(dob ?? '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const now = new Date();
  let age = now.getFullYear() - Number(m[1]);
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  if (md < Number(m[2]) * 100 + Number(m[3])) age--;
  return age >= 0 && age < 130 ? age : null;
}

/**
 * Senior citizen — 60 or more completed years. The SAME line §194A draws for
 * Form 15H vs 15G, and the same one the TDS threshold scan now uses server-side.
 * No date of birth means NOT senior: we cannot show that they are, and a
 * wrongly-raised alert is visible and reversible where a silently-skipped one
 * is neither.
 */
function isSeniorCitizen(dob: unknown): boolean {
  const age = ageFromDob(dob);
  return age != null && age >= 60;
}

function dobLabel(dob: unknown): string | null {
  const m = String(dob ?? '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const pretty = `${m[3]}-${m[2]}-${m[1]}`;
  const age = ageFromDob(dob);
  return age != null ? `${pretty} · ${age} yrs` : pretty;
}

/** Field groups, in display order, derived from the shared field list. */
const CORRECTION_GROUPS = [...new Set(CORRECTABLE_CUSTOMER_FIELDS.map((f) => f.group))];

/** The customer's present value, shaped for the input that renders it —
 *  dates trimmed to YYYY-MM-DD, NULLs to '', booleans left as booleans.
 *  Also the baseline the dirty-check compares against. */
function currentValue(c: any, f: CustomerField): string | boolean {
  const raw = c[f.key];
  if (f.kind === 'boolean') return raw === true;
  if (raw == null) return '';
  if (f.kind === 'date') return String(raw).slice(0, 10);
  return String(raw);
}

/** Customer 360 (docs/05 §5) — profile + bank accounts + KYC + hand-off. */
/**
 * Two-step confirm for an irreversible purge: type DELETE, then give an audit
 * reason. Returns the reason, or null if the operator backed out. Super-admin only.
 */
async function purgeConfirm(promptText: ReturnType<typeof useConfirm>['promptText'], what: string): Promise<string | null> {
  const typed = await promptText({
    title: `⚠️ Permanently delete ${what}`,
    body: 'This erases the record and everything linked to it — schedule, collections, incentives, redemptions, and any locker links or escrow matches. It goes through whatever is attached; nothing blocks it. It CANNOT be undone.',
    label: 'Type DELETE to confirm', placeholder: 'DELETE', minLength: 6, confirmLabel: 'Continue', danger: true,
  });
  if (typed !== 'DELETE') return null;
  return await promptText({
    title: 'Reason for the audit log', body: `Deleting ${what}.`,
    label: 'Reason (required)', confirmLabel: 'Delete permanently', danger: true,
  });
}


/**
 * What this customer holds, and what they can be sold next (owner 2026-08-01:
 * "whether it's an NCD or a locker customer, first a customer profile is
 * created"). The customer is the hub; NCDs and lockers hang off them.
 *
 * The counts are read from data already on the page — no extra call that could
 * leave the strip half-drawn. Only "waiting" is fetched, and it fails soft: an
 * unanswered worklist should not blank out the holdings beside it.
 */
function Holdings({ customerId, pan, apps, approved, onAddNcd }: {
  customerId: number; pan: string | null; apps: any[]; approved: boolean; onAddNcd: () => void;
}) {
  const { can } = useAuth();
  const DEAD = ['Rejected', 'Cancelled', 'Redeemed', 'Matured', 'RolledOver', 'PrematureWithdrawn', 'Transferred'];
  const open = apps.filter((r) => !DEAD.includes(r.status) && !r.archived_at);
  // Approval is what makes an NCD live and starts interest, so an unapproved
  // one is NOT counted here — a headline "2 live · ₹0 outstanding" reads as a
  // bug, and worse, as though money were already earning.
  const active = open.filter((r) => r.status === 'Active');
  const unapproved = open.length - active.length;
  const outstanding = active.reduce((s, r) => s + Number(r.outstanding ?? 0), 0);

  // Same queryKey as LockersCard below — react-query dedupes it to one request.
  const lockers = useQuery({
    queryKey: ['customer-lockers', customerId],
    queryFn: () => api.get<any>(`/api/lockers/customers/${customerId}/lockers`),
    retry: false, enabled: can('lockers:enroll'),
  });
  const lh = lockers.data?.lockerhub;
  const held = (lh?.lockers ?? lh?.applications ?? []) as any[];

  const waiting = useQuery({
    queryKey: ['outstanding', customerId],
    queryFn: () => api.get<{ rows: { kind: string; detail: string }[] }>(`/api/reports/outstanding?customer_id=${customerId}`),
    retry: false,
  });
  const pending = waiting.data?.rows ?? [];

  const tile = 'flex-1 min-w-[150px] px-4 py-3 rounded-lg border border-border bg-bg';
  const label = 'text-[11px] font-semibold text-text-label uppercase tracking-wide';
  // A tile with something behind it becomes a button down to that section
  // (owner 2026-08-19: "clicking on the tile … should get me the investment
  // details and locker details"). Instant, not smooth — the same reason the
  // "+ NCD investment" jump is instant: a long smooth scroll is slow and some
  // renderers drop it silently. A tile with a count of zero stays inert rather
  // than becoming a button that goes nowhere.
  const jump = (anchor: string) => document.getElementById(anchor)?.scrollIntoView({ block: 'start' });
  const tileBtn = `${tile} text-left hover:border-border-strong hover:bg-surface transition-colors cursor-pointer`;
  return (
    <div className="bg-surface border border-border rounded-lg shadow-card p-5 mb-4">
      <div className="flex flex-wrap gap-3 items-stretch">
        {(() => {
          const body = (
            <>
              <div className={label}>NCDs</div>
              <div className="text-lg font-semibold leading-tight mt-0.5">{active.length} active</div>
              <div className="text-xs text-text-muted mono">{formatINR(outstanding)} outstanding</div>
              {unapproved > 0 && <div className="text-xs text-warn mt-0.5">+{unapproved} not approved yet</div>}
            </>
          );
          return open.length > 0
            ? <button type="button" onClick={() => jump('investments')} title="Show this customer’s investments" className={tileBtn}>{body}</button>
            : <div className={tile}>{body}</div>;
        })()}
        {can('lockers:enroll') && (() => {
          const checking = lockers.isLoading;
          const failed = lockers.isError || lockers.data?.lockerhub_error;
          const body = (
            <>
              <div className={label}>Lockers</div>
              {checking
                ? <div className="text-sm text-text-muted mt-1">Checking…</div>
                : failed
                  // Never claim "0 lockers" when we simply could not ask — a wrong
                  // zero here is what makes someone open a duplicate application.
                  ? <div className="text-sm text-warn mt-1">Couldn’t check</div>
                  : <>
                      <div className="text-lg font-semibold leading-tight mt-0.5">{held.length}</div>
                      <div className="text-xs text-text-muted truncate">
                        {held.length === 0 ? 'none yet'
                          : held.map((l) => l.locker_no ?? l.locker_number ?? l.status ?? '—').join(', ')}
                      </div>
                    </>}
            </>
          );
          // Only a locker we actually confirmed has a section to jump to — the
          // Lockers card renders nothing at all when there is nothing to show.
          return !checking && !failed && held.length > 0
            ? <button type="button" onClick={() => jump('lockers')} title="Show this customer’s locker details" className={tileBtn}>{body}</button>
            : <div className={tile}>{body}</div>;
        })()}
        <div className={tile}>
          <div className={label}>Waiting</div>
          {pending.length === 0
            ? <div className="text-sm text-text-muted mt-1">{waiting.isError ? '—' : 'Nothing pending'}</div>
            : <>
                <div className="text-lg font-semibold leading-tight mt-0.5 text-warn">{pending.length}</div>
                <Link to="/app/outstanding" className="text-xs text-primary hover:underline">{pending[0]!.detail.split('—')[0]!.trim()}{pending.length > 1 ? ` +${pending.length - 1}` : ''}</Link>
              </>}
        </div>
      </div>
      {/* Adding either product starts here, because this is where staff already
          are once the customer exists. */}
      {(can('applications:create') || can('lockers:enroll')) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {can('applications:create') && (
            <button onClick={onAddNcd} disabled={!approved}
              title={approved ? 'Record a new NCD investment for this customer' : 'The customer profile has to be approved first'}
              className="text-xs bg-primary text-white rounded px-3 py-1.5 font-semibold disabled:opacity-40 hover:bg-primary-hover">
              + NCD investment
            </button>
          )}
          {can('lockers:enroll') && (
            pan
              ? <Link to={`/app/locker-enrollment?pan=${encodeURIComponent(pan)}`}
                  className="text-xs border border-border-strong rounded px-3 py-1.5 font-semibold hover:bg-bg">+ Locker</Link>
              // The enrolment flow finds them by PAN. Without one it would open
              // on a blank form, so say why instead of pretending to prefill.
              : <span title="This customer has no PAN on record, so the locker flow can’t look them up"
                  className="text-xs border border-border rounded px-3 py-1.5 font-semibold opacity-40 cursor-not-allowed">+ Locker</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Whole days from today to a 'YYYY-MM-DD' date, negative once past.
 *
 * Compared as calendar dates in UTC, not timestamps: a lease end is a date, and
 * measuring it against the current clock makes "ends today" read as expired for
 * most of the working day.
 */
function daysUntilDate(iso: string): number | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) / 86400000);
}

/** Lockers this customer holds — LockerHub's record plus OUR pledges/cheques.
 * Fetched separately so a LockerHub outage degrades this card alone. */
function LockersCard({ customerId, customerName }: { customerId: number; customerName: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer-lockers', customerId],
    queryFn: () => api.get<any>(`/api/lockers/customers/${customerId}/lockers`),
    retry: false,
  });
  if (isLoading || !data) return null;
  const pledges = data.pledges ?? [];
  const cheques = data.cheques ?? [];
  const lh = data.lockerhub;
  const lockers = lh?.lockers ?? [];
  // A5 calls these `open_locker_applications`. The card used to look for
  // `applications`, a key their API does not return — so an enrolment still in
  // progress showed nowhere on the customer, and there was no way back into it.
  const openApps = lh?.open_locker_applications ?? lh?.applications ?? [];
  // LockerHub holds a record on this customer's PHONE, but under a different
  // name — so it is not shown as theirs (owner 2026-08-19). The card still
  // appears, saying plainly what exists and whose it is.
  const nameMismatch: string | null = data.lockerhub_name_mismatch ?? null;
  // The mismatched phone-record's own open applications / lockers, so the note
  // can link straight to a pending application (it is NOT on the tenants roster).
  const unmatchedApps: any[] = data.unmatched?.applications ?? [];
  const unmatchedLockers: any[] = data.unmatched?.lockers ?? [];
  if (!pledges.length && !cheques.length && !lockers.length && !openApps.length
      && !data.lockerhub_error && !nameMismatch) return null;
  const card = 'bg-surface border border-border rounded-lg shadow-card p-5 mb-4';
  return (
    // id: the Lockers holdings tile jumps here.
    <div id="lockers" className={`${card} scroll-mt-4`}>
      <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">Lockers</h2>
      {data.lockerhub_error && (
        <div className="text-xs text-warn mb-2">Couldn’t reach LockerHub — showing what NCD holds. ({String(data.lockerhub_error).slice(0, 80)})</div>
      )}
      {nameMismatch && (
        <div className="text-xs bg-[color:var(--warn-bg)] rounded px-3 py-2 mb-2">
          <div className="text-warn">
            LockerHub has a record on this phone number under the name{' '}
            <span className="font-semibold">{nameMismatch}</span>, which does not match{' '}
            <span className="font-semibold">{customerName}</span> — so nothing from it is counted as this
            customer's.
          </div>
          {/* A pending application is NOT on the tenants roster, so link straight
              to it here — this is the route to review/allot it if it IS them. */}
          {unmatchedApps.length > 0 && (
            <div className="mt-1.5 text-text-muted">
              Locker application{unmatchedApps.length > 1 ? 's' : ''} in progress under that name — open to review, take payment, or allot:
              <div className="mt-1 flex flex-col gap-1">
                {unmatchedApps.map((a: any, i: number) => (
                  <span key={a.id ?? i} className="flex flex-wrap gap-x-2 items-center">
                    {a.id
                      ? <Link to={`/app/locker-enrollment?application_id=${encodeURIComponent(String(a.id))}`} className="text-primary hover:underline font-mono text-xs" title="Open this locker application">{a.application_no ?? a.id}</Link>
                      : <span className="font-mono text-xs">{a.application_no ?? '—'}</span>}
                    {a.status && <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{a.status}</span>}
                    {a.locker_size && <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{a.locker_size}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {unmatchedLockers.length > 0 && (
            <div className="mt-1.5 text-text-muted">
              An allotted locker under that name is on the <Link to="/app/locker-tenants" className="text-primary hover:underline">Locker Tenants</Link> list — link it there if it's the same person.
            </div>
          )}
        </div>
      )}
      {lockers.length > 0 && (
        <div className="text-sm mb-3">
          {lockers.map((l: any, i: number) => {
            // LockerHub's customer record (A5) carries the money and the lease
            // for every locker they hold — annual_rent, deposit, lease_start,
            // lease_end. This card fetched all four and drew none of them, so
            // "what is this customer paying, and until when" could not be
            // answered from the customer's own page (owner 2026-08-03).
            const ends = String(l.lease_end ?? '').slice(0, 10) || null;
            const d = ends ? daysUntilDate(ends) : null;
            return (
              <div key={i} className="border-b border-border last:border-0 py-1.5">
                <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                  <span className="font-medium">{l.locker_no ?? l.locker_number ?? l.application_id ?? 'Locker'}</span>
                  {l.branch_name && <span className="text-text-muted text-xs">{l.branch_name}</span>}
                  {l.locker_size && <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{l.locker_size}</span>}
                  {(l.account_status ?? l.status ?? l.application_status) && (
                    <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{l.account_status ?? l.status ?? l.application_status}</span>
                  )}
                  {/* Renewal state, on the customer's own page — the same fact
                      the renewals worklist is built on, so whoever opens this
                      customer for any reason still sees the lease has lapsed. */}
                  {d != null && d < 0 && (
                    <Link to="/app/locker-renewals" className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--danger-bg)] text-danger font-semibold">
                      Rent overdue {Math.abs(d)}d
                    </Link>
                  )}
                  {d != null && d >= 0 && d <= 30 && (
                    <Link to="/app/locker-renewals" className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn font-semibold">
                      Renews in {d}d
                    </Link>
                  )}
                </div>
                {/* NCD lockers are rent-only (owner 2026-08-22) — deposit is
                    never collected, so it isn't shown. */}
                {(l.annual_rent != null || ends) && (
                  <div className="text-xs text-text-muted mt-0.5 flex flex-wrap gap-x-3">
                    {l.annual_rent != null && <span>Rent <span className="mono text-text">{formatINR(l.annual_rent)}</span>/yr</span>}
                    {ends && <span>Lease {l.lease_start ? `${String(l.lease_start).slice(0, 10)} → ` : 'to '}{ends}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {openApps.length > 0 && (
        <>
          {/* An enrolment part-way through. This is the route back into it —
              to take a payment, allot, or send the agreement for e-Signing. */}
          <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1">Locker applications in progress</div>
          <div className="text-sm mb-3">
            {openApps.map((a: any, i: number) => (
              <div key={a.id ?? i} className="flex flex-wrap gap-x-3 gap-y-1 items-center border-b border-border last:border-0 py-1.5">
                {a.id
                  ? <Link to={`/app/locker-enrollment?application_id=${encodeURIComponent(String(a.id))}`}
                      className="text-primary hover:underline font-mono text-xs"
                      title="Open this locker application — payments, allotment and the e-Sign agreement">
                      {a.application_no ?? a.id}
                    </Link>
                  : <span className="font-mono text-xs">{a.application_no ?? '—'}</span>}
                {a.status && <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn">{a.status}</span>}
                {a.locker_size && <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{a.locker_size}</span>}
                {a.annual_fee != null && <span className="text-xs text-text-muted">Rent <span className="mono text-text">{formatINR(a.annual_fee)}</span></span>}
              </div>
            ))}
          </div>
        </>
      )}
      {pledges.length > 0 && (
        <>
          <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1">NCDs pledged as locker backing</div>
          <div className="text-sm mb-3">
            {pledges.map((p: any) => (
              <div key={p.id} className="flex flex-wrap gap-x-3 items-center border-b border-border last:border-0 py-1.5">
                <Link to={`/app/applications/${p.application_id}`} state={{ from: { path: `/app/customers/${customerId}`, label: customerName } }} className="text-primary hover:underline font-mono text-xs">{p.application_no}</Link>
                <span className="mono">{formatINR(p.linked_amount)}</span>
                <span className="text-xs text-text-muted">locker {p.lockerhub_application_id}{p.locker_no ? ` · ${p.locker_no}` : ''}</span>
                <span className={`text-xs rounded px-1.5 py-0.5 ${p.status === 'active' ? 'bg-[color:var(--warn-bg)] text-warn' : 'bg-bg text-text-muted'}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {cheques.length > 0 && (
        <>
          <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1">Locker cheques</div>
          <div className="text-sm">
            {cheques.map((q: any) => (
              <div key={q.id} className="flex flex-wrap gap-x-3 items-center border-b border-border last:border-0 py-1.5">
                <span className="font-mono text-xs">{q.cheque_no}</span>
                <span className="mono">{formatINR(q.amount)}</span>
                <span className="text-xs text-text-muted">{q.leg} · {q.bank_name ?? '—'}</span>
                <span className={`text-xs rounded px-1.5 py-0.5 ${q.status === 'Cleared' ? 'bg-[color:var(--success-bg)] text-success' : q.status === 'Pending' ? 'bg-[color:var(--warn-bg)] text-warn' : 'bg-bg text-text-muted'}`}>{q.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The nominee the editor has open. `index` -1 is a new one. */
interface NomineeDraft { index: number; full_name: string; relationship: string; share_pct: string; dob: string; phone: string; address: string }

/** The standard relationships, plus whatever is already on the row if it is not
 *  one of them — a picker must never blank a value just because it is unusual. */
function relationshipOptions(current: string): string[] {
  const v = current.trim();
  return v && !NOMINEE_RELATIONSHIPS.includes(v) ? [...NOMINEE_RELATIONSHIPS, v] : NOMINEE_RELATIONSHIPS;
}

export function CustomerDetailPage() {
  const { confirm, promptText } = useConfirm();
  const { id } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useAuth();
  const [msg, setMsg] = useState('');
  // Positive feedback, separate from the red error line — a change that went to
  // a checker succeeded, it just has not taken effect yet.
  const [note, setNote] = useState('');
  const [panel, setPanel] = useState<'correction' | 'handover' | null>(null);
  const [corr, setCorr] = useState<Record<string, string | boolean>>({});
  const [corrReason, setCorrReason] = useState('');
  const [handoverTo, setHandoverTo] = useState('');
  const [handoverReason, setHandoverReason] = useState('');
  // The nominee being added or changed. Null when the editor is closed.
  const [nomEdit, setNomEdit] = useState<NomineeDraft | null>(null);

  const key = ['customer', id];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get<any>(`/api/customers/${id}`) });
  const staff = useQuery({
    queryKey: ['assignable-staff'],
    queryFn: () => api.get<{ rows: { id: number; full_name: string; role: string }[] }>('/api/customers/assignable-staff'),
    enabled: can('customers:handover-request'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const wrap = (p: Promise<unknown>) => p.then(() => { setMsg(''); invalidate(); }).catch((e) => setMsg(e instanceof ApiError ? e.message : 'Failed'));

  if (isLoading) return <div className="text-text-muted">Loading…</div>;
  if (error) return <div className="text-danger">Customer not found or out of scope.</div>;

  const c = data.customer;
  const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';
  const card = 'bg-surface border border-border rounded-lg shadow-card p-5 mb-4';

  /**
   * Write the nominee set. The FIRST nominee for a customer saves immediately;
   * once one exists the server turns ANY change — edit, add or remove — into an
   * approval request (owner 2026-08-19), so the message has to report which
   * happened rather than always claiming "saved".
   */
  async function putNominees(next: NomineeInput[], reason: string) {
    try {
      const r = await api.put<{ applied?: boolean }>(`/api/customers/${id}/nominees`, { nominees: next, reason });
      setMsg('');
      setNomEdit(null);
      setNote(r.applied === false
        ? 'Nominee change sent to the approvals queue — it takes effect once a checker approves it.'
        : '');
      invalidate();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  }

  /** Every nominee on file, in display order, ready to be sent back whole. */
  const nomineeRows = (): NomineeInput[] => (data.nominees ?? []).map(nomineeToInput);

  function saveNomineeDraft(d: NomineeDraft) {
    const name = d.full_name.trim();
    if (name.length < 2) { setMsg('Nominee name is required.'); return; }
    const typed = d.share_pct.trim();
    const share = typed === '' ? null : Number(typed);
    if (share !== null && !(share > 0 && share <= 100)) {
      setMsg('Share % must be a number above 0 and up to 100, or blank to give this nominee the rest.');
      return;
    }

    const rows = nomineeRows();
    // Spread the stored row first so DOB, PAN, phone, address, guardian and KYC
    // id ride through an edit of the three fields shown here.
    const edited: NomineeInput = { ...(d.index >= 0 ? rows[d.index]! : { full_name: name }), full_name: name };
    edited.relationship = d.relationship.trim() || null;
    edited.dob = d.dob.trim() || null;
    edited.phone = d.phone.trim() || null;
    edited.address = d.address.trim() || null;
    if (share !== null) edited.share_pct = share; else delete edited.share_pct;

    const next = d.index >= 0 ? rows.map((r, i) => (i === d.index ? edited : r)) : [...rows, edited];
    void putNominees(next, d.index >= 0 ? `Nominee updated: ${name}` : `Nominee added: ${name}`);
  }

  async function removeNominee(index: number) {
    const n = (data.nominees ?? [])[index];
    const rest = (data.nominees ?? []).length - 1;
    const ok = await confirm({
      title: `Remove ${n?.full_name ?? 'this nominee'}?`,
      body: rest > 0
        ? 'The nominees left keep the shares they state; any left blank split what is unallocated.'
        : 'This customer would be left with NO nominee on file.',
      confirmLabel: 'Remove nominee',
      danger: true,
    });
    if (!ok) return;
    void putNominees(nomineeRows().filter((_, i) => i !== index), `Nominee removed: ${n?.full_name ?? ''}`.trim());
  }

  /**
   * What the shares would come to if the open draft were saved. Blanks are
   * filled by the server from whatever is left, so they are reported as blanks
   * rather than counted as zero — 10 customers on the book are already off 100%
   * and nothing on screen ever said so.
   */
  function shareHint(d: NomineeDraft): string {
    const others = (data.nominees ?? [])
      .filter((_: unknown, i: number) => i !== d.index)
      .map((n: any) => Number(n.share_pct) || 0);
    const typed = d.share_pct.trim();
    const stated = others.filter((x: number) => x > 0).reduce((a: number, b: number) => a + b, 0)
      + (typed === '' ? 0 : Number(typed) || 0);
    const blanks = others.filter((x: number) => !(x > 0)).length + (typed === '' ? 1 : 0);
    if (blanks) return blanks === 1
      ? `${stated}% stated · the blank one takes the remaining ${Math.max(0, 100 - stated)}%`
      : `${stated}% stated · ${blanks} blanks split the remaining ${Math.max(0, 100 - stated)}%`;
    return stated === 100 ? 'Shares total 100%' : `Shares total ${stated}% — not 100%`;
  }

  return (
    <div className="w-full">
      <Link to="/app/customers" className="text-xs text-text-muted hover:text-primary">← Customers</Link>
      <div className="flex items-center gap-3 mt-1">
        <h1 className="text-xl font-bold tracking-tight m-0">{c.full_name}</h1>
        <span className="font-mono text-xs text-text-muted">{c.customer_code}</span>
        <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{c.creation_status}</span>
        {c.archived_at && <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--danger-bg)] text-danger font-semibold">Archived</span>}
        {/* Dematerialisation sign, top-right. Manually set; NULL = not marked. */}
        <div className="ml-auto flex items-center gap-1.5">
          {(() => {
            const dm = c.is_dematerialised;
            const label = dm === true ? 'Dematerialised' : dm === false ? 'Physical' : 'Demat: not marked';
            const cls = dm === true ? 'bg-[color:var(--success-bg)] text-success'
              : dm === false ? 'bg-bg text-text-muted' : 'bg-[color:var(--warn-bg)] text-warn';
            return <span className={`text-xs rounded px-2 py-0.5 font-semibold ${cls}`}>{label}</span>;
          })()}
          {can('customers:update') && (
            <select className="text-xs border border-border-strong rounded px-1.5 py-0.5 bg-surface"
              value={c.is_dematerialised === true ? 'yes' : c.is_dematerialised === false ? 'no' : ''}
              onChange={(e) => {
                const v = e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null;
                wrap(api.patch(`/api/customers/${id}/dematerialised`, { value: v }));
              }}
              title="Mark whether this customer's bond is dematerialised">
              <option value="">Not marked</option>
              <option value="yes">Dematerialised</option>
              <option value="no">Physical</option>
            </select>
          )}
        </div>
      </div>
      {c.archived_at && (
        <div className="text-xs bg-[color:var(--danger-bg)] text-danger rounded px-3 py-2 mt-2">
          This customer is archived — hidden from the book, dashboard and lists. Their investments are archived too.
          {can('customers:delete') && ' Restore or permanently delete below.'}
        </div>
      )}
      {msg && <div className="text-xs text-danger mt-2">{msg}</div>}
      {note && <div className="text-xs text-warn bg-[color:var(--warn-bg)] rounded px-3 py-2 mt-2">{note}</div>}

      <div className="mt-4">
        <Holdings customerId={Number(id)} pan={c.pan ?? null} apps={data.applications ?? []}
          approved={c.creation_status === 'Approved'}
          // Instant, not smooth: the form is ~1500px down, and a smooth scroll
          // that far is both slow and silently dropped by some renderers.
          onAddNcd={() => document.getElementById('new-investment')?.scrollIntoView({ block: 'start' })} />
      </div>

      <div className={card}>
        <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">Profile</h2>
        {/* Grouped, full detail — same field set as the quick-view popup, so the
            profile page shows everything in one place, plus the profile's richer
            Referred-by link and Form-15G/15H validity. */}
        {/* Two columns rather than one long strip (owner 2026-08-19: personal
            details "almost covers up a whole page"). Address, demat and
            nominees ride alongside Personal instead of below it, roughly
            halving the height. CSS grid with a gap — nothing is positioned
            absolutely, so nothing can overlap; below `md` it stacks back into
            one readable column on a narrow screen. */}
        <div className="grid md:grid-cols-2 md:gap-x-8">
          <div>
            <div className="text-[11px] font-semibold text-text-label uppercase tracking-wide mb-1.5">Personal</div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm mb-4">
              <Field label="Phone" value={c.phone} />
              <Field label="Alt. phone" value={c.phone_secondary} />
              <Field label="Email" value={c.email} />
              <Field label="PAN" value={c.pan} />
              {/* Aadhaar shown masked to last 4 — the same posture as the popup. */}
              <Field label="Aadhaar" value={c.aadhaar_last4 ? `XXXX XXXX ${c.aadhaar_last4}` : null} />
              {/* DOB carries the age (drives the senior-citizen TDS slab); parsed as a
                  plain 'YYYY-MM-DD' string, so no timezone shift to guard against. */}
              <Field label="Date of birth" value={dobLabel(c.dob)} />
              <Field label="Gender" value={c.gender} />
              <Field label="Father / spouse" value={c.father_name} />
              <Field label="Occupation" value={c.occupation} />
              <Field label="Category" value={c.investor_category} />
              <Field label="NRI" value={c.is_nri ? 'Yes' : 'No'} />
            </dl>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-text-label uppercase tracking-wide mb-1.5">Address</div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm mb-4">
              <Field label="Address" value={c.address} />
              <Field label="City" value={c.city} />
              <Field label="District" value={c.district} />
              <Field label="State" value={c.state} />
              <Field label="Pincode" value={c.pincode} />
            </dl>

            {/* Demat and nominees live in the profile now (owner 2026-08-19), so
                editing them goes through the same correction → approval road as
                every other profile field instead of a direct save button. */}
            <div className="text-[11px] font-semibold text-text-label uppercase tracking-wide mb-1.5">Demat</div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm mb-4">
              <Field label="DP ID" value={c.demat_dp_id} />
              <Field label="Client ID" value={c.demat_client_id} />
              <Field label="Depository" value={c.depository} />
            </dl>

            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-text-label uppercase tracking-wide">Nominees</span>
              {can('customers:update') && (
                <button onClick={() => { setMsg(''); setNomEdit({ index: -1, full_name: '', relationship: '', share_pct: '', dob: '', phone: '', address: '' }); }}
                  className="text-xs text-primary hover:underline"
                  title={(data.nominees ?? []).length
                    ? 'Nominee changes go to a checker before they take effect'
                    : 'Record the first nominee for this customer'}>
                  + Add
                </button>
              )}
            </div>
            <ul className="text-sm mb-4 mt-0.5">
              {(data.nominees ?? []).map((n: any, i: number) => (
                <li key={n.id} className="flex items-baseline gap-3 py-0.5">
                  <span className="min-w-0">
                    <span className="font-medium">{n.full_name}</span>
                    <span className="text-text-muted"> — {Number(n.share_pct) || 0}%{n.relationship ? ` · ${n.relationship}` : ''}</span>
                  </span>
                  {can('customers:update') && (
                    <span className="ml-auto shrink-0 flex gap-2">
                      <button
                        onClick={() => { setMsg(''); setNomEdit({ index: i, full_name: n.full_name ?? '', relationship: n.relationship ?? '', share_pct: Number(n.share_pct) > 0 ? String(Number(n.share_pct)) : '', dob: String(n.dob ?? '').slice(0, 10), phone: n.phone ?? '', address: n.address ?? '' }); }}
                        className="text-xs text-primary hover:underline">Edit</button>
                      <button onClick={() => void removeNominee(i)}
                        className="text-xs text-danger hover:underline">Remove</button>
                    </span>
                  )}
                </li>
              ))}
              {!(data.nominees ?? []).length && <li className="text-text-muted">—</li>}
            </ul>
            {nomEdit && (
              <div className="border border-border rounded p-3 mb-4 bg-bg">
                <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">
                  {nomEdit.index >= 0 ? 'Edit nominee' : 'Add nominee'}
                  {(data.nominees ?? []).length > 0 && (
                    <span className="ml-2 font-normal normal-case tracking-normal text-text-muted">· needs a checker</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-xs text-text-muted">
                    Full name
                    <input autoFocus className={`${inp} w-full mt-1`} value={nomEdit.full_name}
                      onChange={(e) => setNomEdit({ ...nomEdit, full_name: e.target.value })} />
                  </label>
                  <label className="text-xs text-text-muted">
                    Relationship
                    <select className={`${inp} w-full mt-1`} value={nomEdit.relationship}
                      onChange={(e) => setNomEdit({ ...nomEdit, relationship: e.target.value })}>
                      <option value="">—</option>
                      {relationshipOptions(nomEdit.relationship).map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-text-muted">
                    Share %
                    <input className={`${inp} w-full mt-1`} value={nomEdit.share_pct} placeholder="blank = the rest"
                      onChange={(e) => setNomEdit({ ...nomEdit, share_pct: e.target.value })} />
                  </label>
                  <label className="text-xs text-text-muted">
                    Date of birth
                    <input type="date" className={`${inp} w-full mt-1`} value={nomEdit.dob}
                      onChange={(e) => setNomEdit({ ...nomEdit, dob: e.target.value })} />
                  </label>
                  <label className="text-xs text-text-muted">
                    Phone
                    <input className={`${inp} w-full mt-1`} value={nomEdit.phone} maxLength={10} inputMode="numeric"
                      onChange={(e) => setNomEdit({ ...nomEdit, phone: e.target.value.replace(/\D/g, '') })} />
                  </label>
                  <label className="text-xs text-text-muted col-span-3">
                    Address
                    <input className={`${inp} w-full mt-1`} value={nomEdit.address}
                      onChange={(e) => setNomEdit({ ...nomEdit, address: e.target.value })} />
                  </label>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => saveNomineeDraft(nomEdit)}
                    className="text-xs bg-primary text-white rounded px-4 py-1.5 hover:bg-primary-hover">Save nominee</button>
                  <button onClick={() => { setNomEdit(null); setMsg(''); }}
                    className="text-xs border border-border rounded px-3 py-1.5 hover:bg-surface">Cancel</button>
                  <span className="text-xs text-text-muted ml-auto">{shareHint(nomEdit)}</span>
                </div>
                {/* The other details this nominee carries are captured by the
                    enrolment wizard and are NOT shown here — saying so is the
                    only way staff can tell they survive an edit. */}
                <div className="text-[11px] text-text-muted mt-2">
                  DOB, PAN, phone, address and KYC id stay as they are.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-[11px] font-semibold text-text-label uppercase tracking-wide mb-1.5">Status &amp; attribution</div>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <Field label="KYC" value={c.kyc_status} />
          <Field label="Active" value={c.is_active ? 'Yes' : 'No'} />
          <Field label="CKYC no." value={c.ckyc_number} />
          {/* Tax position — decides TDS on every payout, so it belongs here. */}
          <Field label="TDS applicable" value={c.tds_applicable === false ? 'No' : 'Yes'} />
          <Field label="Form 15G/15H" value={c.tax_form
            ? `${c.tax_form}${c.tax_form_expires_on ? ` · valid to ${String(c.tax_form_expires_on).slice(0, 10)}` : ' · no validity date (ignored)'}`
            : null} />
          {/* Who brought this customer in — staff or agent (owner 2026-07-24). */}
          <Field label="Enrolled by" value={c.enrolled_by_name
            ? `${c.enrolled_by_name}${c.enrolled_by_kind === 'agent' ? ` (agent${c.enrolled_by_agent_code ? ' ' + c.enrolled_by_agent_code : ''})` : ' (staff)'}`
            : null} />
          {/* Referred by is NOT the same as enrolled by: staff enrol, but the
              customer may have been introduced by another customer or an agent.
              Free text on the record, resolved to a person server-side. */}
          <div>
            <dt className="text-xs text-text-label uppercase tracking-wide">Referred by</dt>
            <dd className="text-sm m-0 mt-0.5">
              {!data.referredBy ? <span className="text-text-muted">—</span>
                : data.referredBy.kind === 'customer'
                  ? <Link to={`/app/customers/${data.referredBy.id}`} className="text-primary hover:underline">
                      {data.referredBy.name} <span className="text-text-muted">({data.referredBy.code})</span>
                    </Link>
                : data.referredBy.kind === 'agent'
                  ? <>{data.referredBy.name} <span className="text-text-muted">(agent {data.referredBy.code})</span></>
                : data.referredBy.kind === 'staff'
                  ? <>{data.referredBy.name} <span className="text-text-muted">(staff)</span></>
                : <span title="Recorded as free text — no matching customer, agent or staff member">
                    {data.referredBy.text} <span className="text-text-muted">(unmatched)</span>
                  </span>}
            </dd>
          </div>
        </dl>
        <div className="flex gap-2 mt-4">
          {can('kyc:verify') && c.kyc_status !== 'Verified' && (
            <button onClick={() => wrap(api.post(`/api/customers/${id}/kyc/verify`))} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">✓ Verify KYC</button>
          )}
          {can('kyc:reject') && c.kyc_status !== 'Rejected' && (
            <button onClick={async () => {
              const reason = await promptText({ title: 'Reject this KYC?', body: 'The customer stays on the book; their KYC is marked rejected.', label: 'Reason', confirmLabel: 'Reject KYC', danger: true });
              if (reason) wrap(api.post(`/api/customers/${id}/kyc/reject`, { reason }));
            }} className="text-xs border border-border text-danger rounded px-3 py-1.5 hover:bg-[color:var(--danger-bg)]">✗ Reject KYC</button>
          )}
          {/* Customer creation needs no approval (owner 2026-07-21) — the customer
              is live on creation; the only approval gate is the investment. */}
          {can('customers:correction-request') && c.creation_status !== 'Draft' && (
            <button onClick={() => setPanel(panel === 'correction' ? null : 'correction')} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">Request correction</button>
          )}
          {can('customers:update') && (
            <button onClick={async () => {
              // Both answers ACT — the two buttons are the two settings, not
              // yes/no. Declining leads to the 15G/15H details, which can still
              // be backed out of.
              const applies = await confirm({
                title: "Deduct TDS on this customer's payouts?",
                body: `Currently: ${c.tds_applicable === false ? 'NOT deducted' : 'deducted'}.`,
                confirmLabel: 'Yes, deduct TDS', cancelLabel: 'No — 15G/15H on file',
              });
              if (applies) {
                wrap(api.patch(`/api/customers/${id}/tax`, { tds_applicable: true, tax_form: null, tax_form_expires_on: null }));
                return;
              }
              const form = await promptText({ title: 'Which form is on file?', label: '15G or 15H', defaultValue: c.tax_form ?? '15G', minLength: 3, confirmLabel: 'Next' });
              if (form === null) return;
              const upper = form.trim().toUpperCase();
              if (!['15G', '15H'].includes(upper)) { setMsg('Tax form must be 15G or 15H.'); return; }
              const until = await promptText({
                title: `${upper} — valid until`, body: '15G/15H run per financial year, so a validity date is required.',
                label: 'Valid until', inputType: 'date', defaultValue: c.tax_form_expires_on ? String(c.tax_form_expires_on).slice(0, 10) : '',
                minLength: 10, confirmLabel: 'Save',
              });
              if (until === null) return;
              if (!/^\d{4}-\d{2}-\d{2}$/.test(until.trim())) { setMsg('Enter the validity date as YYYY-MM-DD — without it the form is ignored.'); return; }
              wrap(api.patch(`/api/customers/${id}/tax`, { tds_applicable: false, tax_form: upper, tax_form_expires_on: until.trim() }));
            }} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">Set TDS / 15G-15H</button>
          )}
          {can('customers:handover-request') && (
            <button onClick={() => setPanel(panel === 'handover' ? null : 'handover')} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">Request handover</button>
          )}
          {/* Super-admin-only power tools (customers:delete). */}
          {can('customers:delete') && (c.archived_at
            ? <button onClick={() => wrap(api.post(`/api/customers/${id}/unarchive`))} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg ml-auto">♻ Restore customer</button>
            : <button onClick={async () => {
                const r = await promptText({
                  title: 'Archive this customer?',
                  body: 'It (and its investments) will be hidden from the book but stay recoverable.',
                  label: 'Reason (optional)', minLength: 0, confirmLabel: 'Archive',
                });
                if (r !== null) wrap(api.post(`/api/customers/${id}/archive`, { reason: r || undefined }));
              }} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg ml-auto">🗄 Archive</button>
          )}
          {can('customers:delete') && (
            <button onClick={async () => { const reason = await purgeConfirm(promptText, `customer ${c.full_name} (${c.customer_code}) and ALL their investments`); if (reason) wrap(api.del(`/api/customers/${id}`, { confirm: true, reason }).then(() => nav('/app/customers'))); }}
              className="text-xs border border-danger text-danger rounded px-3 py-1.5 hover:bg-[color:var(--danger-bg)]">🗑 Delete permanently</button>
          )}
        </div>

        {panel === 'correction' && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Correction request (needs approval)</div>
            <p className="text-xs text-text-muted mb-3">Edit anything that needs fixing — only the fields you actually change are sent for approval.</p>
            {CORRECTION_GROUPS.map((g) => (
              <div key={g} className="mb-3">
                <div className="text-xs font-semibold text-text-label mb-1">{g}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-3xl">
                  {CORRECTABLE_CUSTOMER_FIELDS.filter((f) => f.group === g).map((f) => {
                    const cur = currentValue(c, f);
                    const val = corr[f.key] ?? cur;
                    const dirty = val !== cur;
                    const set = (v: string | boolean) => setCorr((s) => ({ ...s, [f.key]: v }));
                    return (
                      <label key={f.key} className={`text-xs ${dirty ? 'text-primary font-semibold' : 'text-text-muted'}`}>
                        {f.label}
                        {f.key === 'referred_by_text' ? (
                          // Searchable staff/agent dropdown (free text still allowed),
                          // so reassigning "Referred by" offers the real names.
                          <div className="mt-1"><ReferredByPicker value={String(val)} onChange={(v) => set(v)} className={`${inp} w-full`} /></div>
                        ) : f.kind === 'boolean' ? (
                          <div className="mt-1"><input type="checkbox" checked={val === true} onChange={(e) => set(e.target.checked)} /></div>
                        ) : f.kind === 'select' ? (
                          <select className={`${inp} w-full mt-1`} value={String(val)} onChange={(e) => set(e.target.value)}>
                            <option value="">—</option>
                            {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input className={`${inp} w-full mt-1`} type={f.kind === 'date' ? 'date' : f.kind === 'email' ? 'email' : 'text'}
                            maxLength={f.maxLength} placeholder={f.hint} value={String(val)}
                            onChange={(e) => set(f.uppercase ? e.target.value.toUpperCase() : e.target.value)} />
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            <label className="text-xs text-text-muted block max-w-3xl">
              Reason
              <input className={`${inp} w-full mt-1`} value={corrReason} onChange={(e) => setCorrReason(e.target.value)} placeholder="Why is this correction needed?" />
            </label>
            <button
              disabled={corrReason.trim().length < 2}
              onClick={() => {
                const changes: Record<string, string | boolean> = {};
                for (const f of CORRECTABLE_CUSTOMER_FIELDS) {
                  if (!(f.key in corr)) continue;
                  const v = corr[f.key]!;
                  if (v !== currentValue(c, f)) changes[f.key] = v;
                }
                if (!Object.keys(changes).length) { setMsg('No fields changed.'); return; }
                wrap(api.post(`/api/customers/${id}/correction-request`, { changes, reason: corrReason.trim() }).then(() => { setPanel(null); setCorr({}); setCorrReason(''); }));
              }}
              className="mt-3 text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">Submit correction</button>
          </div>
        )}

        {panel === 'handover' && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="text-xs font-semibold text-text-label uppercase tracking-wide mb-2">Handover request (needs approval)</div>
            <div className="flex flex-wrap gap-2 items-center">
              <select className={inp} value={handoverTo} onChange={(e) => setHandoverTo(e.target.value)}>
                <option value="">Hand over to…</option>
                {(staff.data?.rows ?? []).map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
              </select>
              <input className={`${inp} w-64`} value={handoverReason} onChange={(e) => setHandoverReason(e.target.value)} placeholder="Reason" />
              <button disabled={!handoverTo || handoverReason.trim().length < 2}
                onClick={() => wrap(api.post(`/api/customers/${id}/handover-request`, { toUserId: Number(handoverTo), reason: handoverReason.trim() }).then(() => { setPanel(null); setHandoverTo(''); setHandoverReason(''); }))}
                className="text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">Submit handover</button>
            </div>
          </div>
        )}
      </div>

      {/* Products first and together — an NCD and a locker are two things the
          same customer holds, so they read as one section rather than one card
          up here and another below the KYC paperwork. */}
      <InvestmentsCard rows={data.applications ?? []} customerId={Number(id)} customerName={c.full_name} canDelete={can('applications:delete')} onChange={invalidate} onError={setMsg} />

      <LockersCard customerId={Number(id)} customerName={c.full_name} />

      {can('applications:create') && c.creation_status === 'Approved' && (
        <NewInvestment customerId={Number(id)} custNoTds={c.tds_applicable === false}
          // A senior citizen is out of scope for the ₹30L TDS question entirely
          // (owner 2026-08-11), so the prompt must not appear for one. This is
          // the OTHER door into a TDS flip — leaving it open would let staff
          // flip a customer the nightly scan has been told to leave alone.
          isSenior={isSeniorCitizen(c.dob)} />
      )}

      <div className={card}>
        <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">Bank accounts</h2>
        <BankAccounts
          customerId={Number(id)}
          accounts={data.bankAccounts}
          canEdit={can('customers:update')}
          canDelete={can('customers:delete')}
          onChange={invalidate}
          onError={setMsg}
        />
      </div>


      <CustomerKyc customerId={Number(id)} data={data} onChange={invalidate} can={can} />
    </div>
  );
}

const appPill: Record<string, string> = {
  Active: 'bg-[color:var(--success-bg)] text-success',
  Redeemed: 'bg-bg text-text-muted', Matured: 'bg-bg text-text-muted',
  Rejected: 'bg-[color:var(--danger-bg)] text-danger', Cancelled: 'bg-[color:var(--danger-bg)] text-danger',
};

/** The customer's investments — every application, newest first, linking to the
 * application page. LIVE statuses total into the header line. */
function InvestmentsCard({ rows, customerId, customerName, canDelete, onChange, onError }: { rows: any[]; customerId: number; customerName: string; canDelete: boolean; onChange: () => void; onError: (m: string) => void }) {
  const { promptText } = useConfirm();
  const { can } = useAuth();
  const nav = useNavigate();
  const DEAD = ['Rejected', 'Cancelled', 'Redeemed', 'Matured', 'RolledOver', 'PrematureWithdrawn', 'Transferred'];
  const live = rows.filter((r) => !DEAD.includes(r.status) && !r.archived_at);
  const outstanding = live.reduce((s, r) => s + Number(r.outstanding ?? 0), 0);
  const th = 'py-2 px-3 text-xs font-semibold text-text-label uppercase tracking-wide text-left';
  const td = 'py-2 px-3 align-middle';
  const run = (p: Promise<unknown>) => p.then(() => { onError(''); onChange(); }).catch((e) => onError(e instanceof ApiError ? e.message : 'Failed'));
  const archiveApp = async (r: any) => {
    const reason = await promptText({
      title: `Archive investment ${r.application_no}?`,
      body: 'Hidden from the book but recoverable.',
      label: 'Reason (optional)', minLength: 0, confirmLabel: 'Archive',
    });
    if (reason !== null) run(api.post(`/api/applications/${r.id}/archive`, { reason: reason || undefined }));
  };
  const restoreApp = (r: any) => run(api.post(`/api/applications/${r.id}/unarchive`));
  const purgeApp = async (r: any) => { const reason = await purgeConfirm(promptText, `investment ${r.application_no}`); if (reason) run(api.del(`/api/applications/${r.id}`, { confirm: true, reason })); };
  const numOf = (code: string) => parseInt(String(code).replace(/[^0-9]/g, ''), 10) || 0;
  const prettySeries = (code: string) => String(code).replace(/_/g, ' ').replace(/\bROLLOVER\b/i, 'Rollover');
  // Group the customer's investments by series (newest series first).
  const groups = (() => {
    const m = new Map<string, any[]>();
    for (const r of rows) { const k = r.series_code ?? '—'; if (!m.has(k)) m.set(k, []); m.get(k)!.push(r); }
    return [...m.entries()].map(([code, rs]) => ({
      code, series_id: rs[0]?.series_id, subordinate: rs[0]?.is_subordinate === true, rows: rs,
    })).sort((a, b) => numOf(b.code) - numOf(a.code));
  })();
  const liveOf = (rs: any[]) => rs.filter((r) => !DEAD.includes(r.status) && !r.archived_at);
  // Statuses that count as "issued" — a consolidated (filing) bond covers these.
  const ISSUABLE = new Set(['Active', 'Matured', 'Redeemed', 'RolledOver']);
  const [open, setOpen] = useState<Set<string>>(() => new Set(groups.filter((g) => liveOf(g.rows).length > 0).map((g) => g.code)));
  const toggle = (c: string) => setOpen((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  /**
   * The received-date cell. One credit → its date, exactly as before. Several →
   * every credit's own date, because that is what makes the tranches visible on
   * the profile at all: a clubbed debenture is one investment paid for across
   * several days, and each day is when that credit starts earning.
   *
   * DISTINCT dates only — two credits that landed the same day are one date, not
   * the same date printed twice. Falls back to the application's date for legacy
   * rows whose credits carry no date of their own.
   */
  const receivedDates = (r: any) => {
    const credits: Array<{ date: string | null; amount: string | number }> = Array.isArray(r.credits) ? r.credits : [];
    const dated = credits.filter((c) => c.date);
    if (dated.length === 0) return r.date_money_received ? String(r.date_money_received).slice(0, 10) : '—';
    const dates = Array.from(new Set(dated.map((c) => String(c.date).slice(0, 10))));
    if (dates.length === 1) return dates[0];
    const breakup = dated.map((c) => `${String(c.date).slice(0, 10)} — ${formatINR(Number(c.amount))}`).join('\n');
    const shown = dates.slice(0, 3);
    return (
      <span className="inline-flex flex-col leading-tight" title={`Paid in ${dated.length} credits:\n${breakup}`}>
        {shown.map((d) => <span key={d}>{d}</span>)}
        {dates.length > shown.length && (
          <span className="text-text-muted">+{dates.length - shown.length} more</span>
        )}
      </span>
    );
  };

  const investmentRow = (r: any, subordinate = false) => (
    <tr key={r.id} className={`border-b border-border last:border-0 hover:bg-bg cursor-pointer ${r.archived_at ? 'opacity-50' : ''}`}
      onClick={() => nav(`/app/applications/${r.id}`, { state: { from: { path: `/app/customers/${customerId}`, label: customerName } } })}>
      <td className={`${td} font-mono text-xs whitespace-nowrap`}>
        {r.application_no}
        {r.line_count > 1 && <span className="ml-1.5 text-[10px] rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn" title={`Paid in ${r.line_count} credits — open to see the breakup`}>{r.line_count} credits</span>}
      </td>
      <td className={td}>
        <span className={`text-[11px] rounded px-1.5 py-0.5 ${appPill[r.status] ?? 'bg-[color:var(--warn-bg)] text-warn'}`}>{r.status}</span>
        {r.archived_at && <span className="ml-1 text-[11px] rounded px-1.5 py-0.5 bg-[color:var(--danger-bg)] text-danger">Archived</span>}
      </td>
      <td className={`${td} whitespace-nowrap`}>
        {r.esigned_at
          ? <span className="text-[11px] rounded px-1.5 py-0.5 bg-[color:var(--success-bg)] text-success" title={`eSigned on ${String(r.esigned_at).slice(0, 10)}`}>✓ eSigned</span>
          : <span className="text-[11px] rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn" title="Not eSigned yet">Not signed</span>}
        {r.esigned_at && r.has_signed_copy && (
          <a href={`/api/reports/esigned/${r.id}.pdf`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
             className="ml-1.5 text-[11px] text-primary hover:underline">view</a>
        )}
      </td>
      {/* Received date(s). A clubbed debenture is ONE investment paid for on
          several days, and each of those days is real — it is when that credit
          starts earning. Showing only the application's single date hid the
          tranches entirely, so every credit's date is listed here (owner
          2026-08-26). Up to three inline; beyond that the rest collapse into
          "+N more", with the full breakup — date and amount — on hover. */}
      <td className={`${td} text-xs whitespace-nowrap`}>{receivedDates(r)}</td>
      <td className={`${td} text-right mono`}>{formatINR(r.amount)}</td>
      <td className={`${td} text-right mono font-medium`}>{formatINR(r.outstanding ?? 0)}</td>
      {/* A subordinate series shows each investment's OWN number where it has
          one — 8 were issued under the older per-investment rule — but offers no
          link: the series bond above covers them all now. */}
      {subordinate && (
        <td className={`${td} whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>
          <span className="text-xs mono text-text-muted">{r.bond_serial_no ?? '\u2014'}</span>
        </td>
      )}
      {canDelete && (
        <td className={`${td} text-right whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>
          {r.archived_at
            ? <button onClick={() => restoreApp(r)} className="text-xs text-primary hover:underline mr-3">Restore</button>
            : <button onClick={() => archiveApp(r)} className="text-xs text-primary hover:underline mr-3">Archive</button>}
          <button onClick={() => purgeApp(r)} className="text-xs text-danger hover:underline">Delete</button>
        </td>
      )}
    </tr>
  );

  return (
    // id: the NCDs holdings tile jumps here.
    <div id="investments" className="bg-surface border border-border rounded-lg shadow-card p-5 mb-4 scroll-mt-4">
      <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-1">Investments</h2>
      {rows.length === 0 ? (
        <div className="py-2 text-text-muted text-sm">No investments yet.</div>
      ) : (
        <>
          <div className="text-xs text-text-muted mb-3">
            {live.length} live · outstanding <span className="font-semibold text-text">{formatINR(outstanding)}</span>
            {rows.length > live.length ? ` · ${rows.length - live.length} closed` : ''} · {groups.length} series
          </div>
          <div className="flex flex-col gap-2">
            {groups.map((g) => {
              const gl = liveOf(g.rows);
              const gInvested = g.rows.reduce((s, r) => s + Number(r.amount ?? 0), 0);
              const gOut = gl.reduce((s, r) => s + Number(r.outstanding ?? 0), 0);
              const isOpen = open.has(g.code);
              const hasIssuable = g.rows.some((r) => ISSUABLE.has(r.status));
              return (
                <div key={g.code} className="border border-border rounded-lg overflow-hidden">
                  <div onClick={() => toggle(g.code)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 bg-bg hover:bg-surface text-left cursor-pointer">
                    <span className={`text-text-muted text-xs transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span>
                    <span className="font-semibold text-sm">{prettySeries(g.code)}</span>
                    <span className="text-xs text-text-muted">{g.rows.length} investment{g.rows.length === 1 ? '' : 's'}</span>
                    <span className="ml-auto text-xs text-text-muted">invested <span className="font-medium text-text mono">{formatINR(gInvested)}</span> · outstanding <span className="font-semibold text-text mono">{formatINR(gOut)}</span></span>
                    {/* ONE bond per customer per series (owner 2026-11-03), so this
                        is the bond for a subordinate series, not an extra. */}
                    {hasIssuable && g.series_id && can('bonds:download') && (
                      <a href={`/api/reports/consolidated-bond/${customerId}/${g.series_id}.pdf`} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()} className="text-xs text-primary hover:underline whitespace-nowrap ml-1"
                        title="One filing bond covering all this customer's investments in this series (total value + units)">Consolidated bond ↗</a>
                    )}
                  </div>
                  {isOpen && (
                    <div className="overflow-x-auto border-t border-border">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b border-border">
                            <th className={th}>App no</th><th className={th}>Status</th><th className={th}>eSign</th><th className={th}>Received</th>
                            <th className={`${th} text-right`}>Invested</th><th className={`${th} text-right`}>Outstanding</th>
                            {g.subordinate && <th className={th}>Bond no</th>}
                            {canDelete && <th className={`${th} text-right`}></th>}
                          </tr>
                        </thead>
                        <tbody>{g.rows.map((r) => investmentRow(r, g.subordinate))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function CustomerKyc({ customerId, data, onChange, can }: { customerId: number; data: any; onChange: () => void; can: (...p: any[]) => boolean }) {
  const { promptText } = useConfirm();
  const [msg, setMsg] = useState('');
  const [docType, setDocType] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const wrap = (p: Promise<unknown>) => p.then(() => { setMsg(''); onChange(); }).catch((e) => setMsg(e instanceof ApiError ? e.message : 'Failed'));
  const card = 'bg-surface border border-border rounded-lg shadow-card p-5 mb-4';
  const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';

  /** Upload against the type the operator picked. The type is not cosmetic —
   *  background verification looks up `customer_photo`, `pan_card` etc. by
   *  name, so a photo filed as generic 'KYC' is invisible to it. */
  function uploadDoc(doc_type: string) {
    const picker = document.createElement('input'); picker.type = 'file'; picker.accept = 'image/*,.pdf';
    picker.onchange = () => {
      const file = picker.files?.[0]; if (!file) return;
      if (file.size > 4 * 1024 * 1024) { setMsg('Document must be under 4 MB.'); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const data_base64 = String(reader.result).split(',')[1] ?? '';
        void wrap(api.post(`/api/customers/${customerId}/documents`, { doc_type, filename: file.name, mime: file.type || 'application/octet-stream', data_base64 })
          .then(() => { setUploadOpen(false); setDocType(''); }));
      };
      reader.readAsDataURL(file);
    };
    picker.click();
  }

  return (
    <>
      {msg && <div className="text-xs text-danger mb-2">{msg}</div>}

      {/* KYC — documents + verification */}
      <div className={card}>
        <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">KYC</h2>
        <div>
          <div className="flex items-center justify-between"><span className="font-semibold text-sm">Documents</span>{can('customers:update') && <button onClick={() => setUploadOpen((o) => !o)} className="text-xs text-primary hover:underline">+ Upload</button>}</div>
          <ul className="mt-1 text-text-muted text-sm">{(data.documents ?? []).map((d: any) => <li key={d.id}><a href={`/api/customers/${customerId}/documents/${d.id}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{docLabel(d.doc_type)} — {d.original_filename ?? d.id}</a> <span className="text-xs">({d.origin})</span></li>)}{!(data.documents ?? []).length && <li>None</li>}</ul>
          {uploadOpen && (
            <div className="mt-3 border-t border-border pt-3 flex flex-wrap gap-2 items-center">
              <label className="text-xs text-text-muted">What are you uploading?</label>
              <select className={inp} value={docType} onChange={(e) => setDocType(e.target.value)}>
                <option value="">Select document type…</option>
                {KYC_DOCUMENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <button disabled={!docType} onClick={() => uploadDoc(docType)}
                className="text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">Choose file…</button>
              <button onClick={() => { setUploadOpen(false); setDocType(''); }} className="text-xs text-text-muted hover:underline">Cancel</button>
              <span className="text-xs text-text-muted w-full">JPEG, PNG, WebP or PDF, up to 4 MB.</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          {can('kyc:verify') && <button onClick={() => wrap(api.post(`/api/customers/${customerId}/kyc/digilocker/start`).then(() => api.post(`/api/customers/${customerId}/kyc/digilocker/complete`)))} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg">DigiLocker verify</button>}
          {can('customers:deactivate') && !data.customer.is_deceased && <button onClick={async () => {
            const d = await promptText({ title: 'Mark this customer deceased', body: 'Payouts stop until a transformation is processed.', label: 'Deceased date', inputType: 'date', minLength: 10, confirmLabel: 'Mark deceased', danger: true });
            if (d) wrap(api.post(`/api/customers/${customerId}/deceased`, { deceased_date: d }));
          }} className="text-xs border border-border rounded px-3 py-1.5 hover:bg-bg text-danger">Mark deceased</button>}
        </div>
      </div>
    </>
  );
}

function NewInvestment({ customerId, custNoTds, isSenior }: { customerId: number; custNoTds: boolean; isSenior: boolean }) {
  const nav = useNavigate();
  const [seriesId, setSeriesId] = useState('');
  const [schemeId, setSchemeId] = useState('');
  const [amount, setAmount] = useState('');
  // A No-TDS customer investing over ₹30L: the creator is asked, per investment,
  // whether TDS should apply after all. '' until they answer; the create is
  // blocked until they do. 'yes' stamps a per-line override (the customer's own
  // No-TDS status is left untouched).
  const [applyTds, setApplyTds] = useState<'' | 'yes' | 'no'>('');
  const over30L = amount !== '' && Number(amount) > 30 * LAKH;
  // Seniors (60+) are exempt from the ₹30L alert, so they are never asked.
  const tdsPrompt = custNoTds && over30L && !isSenior;
  // NCDs are ISSUED in whole ₹1,00,000 units, but a single credit need not be
  // one (owner 2026-08-01): money arrives in parts and is clubbed. So this is a
  // WARNING, not a block — the server accepts any positive amount, and approval
  // is what refuses a total that isn't a whole unit. Blocking here would force
  // staff to record a figure the bank statement doesn't show.
  // Is this an NCD or a Subordinate Bond (owner spec 2026-08-10)? A subordinate
  // bond belongs to no series, is priced from its own product master, and is
  // numbered SOB-.
  const [productType, setProductType] = useState<'ncd' | 'subordinate_bond'>('ncd');
  const isSob = productType === 'subordinate_bond';
  const [sobProductId, setSobProductId] = useState('');
  const isWholeUnit = amount !== '' && Number(amount) >= LAKH && Math.round(Number(amount) * 100) % (LAKH * 100) === 0;
  // Subordinate bonds have NO whole-unit rule, so the part-payment warning must
  // not appear for one — it would tell staff to top up an investment that is
  // already complete.
  const isPartPayment = !isSob && amount !== '' && Number(amount) > 0 && !isWholeUnit;
  const [dateReceived, setDateReceived] = useState('');
  const [clubWith, setClubWith] = useState('');
  const [lockerDeposit, setLockerDeposit] = useState(false);
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [creditedBank, setCreditedBank] = useState('');   // which Dhanam account received it (optional)
  const [receipt, setReceipt] = useState<File | null>(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  // /api/banks, NOT /api/products/banks — productsRouter is mounted at `/api`,
  // so its /banks route is /api/banks. The old path 404'd, react-query left
  // `data` undefined, and `?? []` turned a broken request into an empty
  // dropdown that looked like "no accounts configured".
  const collectionBanks = useQuery({ queryKey: ['collection-banks'], queryFn: () => api.get<{ rows: any[] }>('/api/banks') });
  const series = useQuery({ queryKey: ['series'], queryFn: () => api.get<{ rows: any[] }>('/api/series') });
  const schemes = useQuery({ queryKey: ['schemes'], queryFn: () => api.get<{ rows: any[] }>('/api/schemes') });
  // Same mount caveat as /api/banks above — productsRouter is on `/api`.
  const sobProducts = useQuery({ queryKey: ['sob-products'], queryFn: () => api.get<{ rows: any[] }>('/api/sob-products') });
  // In-flight applications in the chosen series this new line could club into
  // (append to an existing pre-allotment application instead of a new one).
  const candidates = useQuery({
    queryKey: ['clubbing', customerId, seriesId],
    queryFn: () => api.get<{ rows: any[] }>(`/api/applications/clubbing-candidates?customer_id=${customerId}&series_id=${seriesId}`),
    enabled: !!seriesId,
  });
  const readFileB64 = (file: File) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject; r.readAsDataURL(file);
  });
  const create = useMutation({
    mutationFn: async () => {
      // The payment evidence (credited date, method, reference, receipt photo)
      // is mandatory. The receipt travels WITH the create — the API stores it in
      // the same transaction, so no investment can exist without one.
      if (!receipt) throw new ApiError('VALIDATION', 400, 'Receipt photo is required');
      if (receipt.size > 4 * 1024 * 1024) throw new ApiError('too_large', 400, 'Receipt must be under 4 MB');
      return api.post<{ id: number }>('/api/applications', {
        customer_id: customerId, amount: Number(amount),
        // A subordinate bond sends its product and NO series/scheme; an NCD the
        // reverse. The database constraint refuses any other combination.
        ...(isSob
          ? { product_type: 'subordinate_bond', sob_product_id: Number(sobProductId) }
          : { series_id: Number(seriesId), scheme_id: Number(schemeId) }),
        date_money_received: dateReceived,
        collection_method: method.trim(),
        collection_reference: reference.trim(),
        ...(creditedBank ? { collection_bank_id: Number(creditedBank) } : {}),
        receipt: { filename: receipt.name, mime: receipt.type || 'application/octet-stream', data_base64: await readFileB64(receipt) },
        ...(clubWith ? { club_with_application_id: Number(clubWith) } : {}),
        ...(lockerDeposit ? { is_locker_deposit: true } : {}),
        // Only when the >₹30L prompt was shown AND answered "yes" — this marks
        // the WHOLE customer as TDS-applicable (all their investments), not just
        // this one.
        ...(tdsPrompt && applyTds === 'yes' ? { mark_customer_tds: true } : {}),
      });
    },
    onSuccess: (r) => {
      // Clubbing into a LIVE investment doesn't change it yet — it's queued for
      // Admin/CXO approval. Tell the maker here (they can't see the Approvals
      // page) rather than navigating to an unchanged investment.
      if ((r as { pending_approval?: boolean }).pending_approval) {
        setNotice('This credit has been sent for Admin/CXO approval. The investment stays unchanged until a checker approves it.');
        return;
      }
      nav(`/app/applications/${r.id}`);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Failed'),
  });
  // Mandatory before Create is allowed — mirrors the API schema.
  const missingRequired = () => [
    isSob && !sobProductId && 'subordinate bond product',
    !isSob && !seriesId && 'series',
    !isSob && !schemeId && 'scheme',
    !dateReceived && 'credited date',
    !method && 'payment method',
    !reference.trim() && 'reference / cheque no.',
    !receipt && 'receipt photo',
    tdsPrompt && applyTds === '' && 'TDS decision (over ₹30L)',
  ].filter((f): f is string => !!f);
  const sel = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';
  const clubOptions = candidates.data?.rows ?? [];
  return (
    <div id="new-investment" className="bg-surface border border-border rounded-lg shadow-card p-5 mb-4 scroll-mt-4">
      <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide mb-3">New investment</h2>
      {/* NCD or Subordinate Bond. Chosen FIRST because it changes what the rest
          of the form asks for — a subordinate bond has no series and no scheme,
          and no ₹1,00,000 unit rule. Switching clears the other product's
          fields so a stale series can never ride along on a sub bond. */}
      <div className="flex flex-wrap gap-1 items-center mb-3">
        {([['ncd', 'NCD'], ['subordinate_bond', 'Subordinate Bond']] as const).map(([v, label]) => (
          <button key={v} type="button"
            onClick={() => { setProductType(v); setClubWith(''); setSeriesId(''); setSchemeId(''); setSobProductId(''); }}
            className={`text-xs rounded px-3 py-1.5 border font-semibold ${productType === v
              ? 'border-primary text-primary bg-[color:var(--primary-bg,transparent)]'
              : 'border-border text-text-muted hover:bg-bg'}`}>
            {label}
          </button>
        ))}
        {isSob && <span className="text-xs text-text-muted">Not part of any NCD series · numbered SOB-</span>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {isSob ? (
          <select className={sel} value={sobProductId} onChange={(e) => setSobProductId(e.target.value)}>
            <option value="">Subordinate bond product… *</option>
            {/* Retired products are hidden: existing investments on one are
                unaffected, since the line holds its own rate snapshot. */}
            {(sobProducts.data?.rows ?? []).filter((p) => p.is_active !== false).map((p) => (
              <option key={p.id} value={p.id}>{p.code} ({p.coupon_rate_pct}% · {p.tenure_months}m)</option>
            ))}
          </select>
        ) : (
          <>
            <select className={sel} value={seriesId} onChange={(e) => { setSeriesId(e.target.value); setClubWith(''); }}>
              <option value="">Series…</option>
              {/* Only an OPEN series can take a new investment (closed/allotted are locked). */}
              {(series.data?.rows ?? []).filter((s) => s.status === 'Open').map((s) => <option key={s.id} value={s.id}>{s.code}</option>)}
            </select>
            <select className={sel} value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
              <option value="">Scheme…</option>
              {/* Rate AND term: this showed only the code and the rate, so a 36-month
                   and a 60-month scheme at the same rate looked identical, and the code
                   itself can be stale (13_M36_13 is 60 months as of 2026-11-02 — the code
                   is matched by the integration and the importer, so it cannot be renamed).
                   Whoever enrols has to be able to see what they are selling. */}
              {/* Retired schemes must not be selectable (owner 2026-11-03: "hide all
                   other schemes"). /api/schemes serves the Masters screen too, which
                   has to list every scheme to manage it, so the filter belongs here
                   and not in the endpoint. A scheme with no flag at all still shows,
                   so an older row cannot vanish from enrolment by accident. */}
              {(schemes.data?.rows ?? []).filter((s) => s.is_active !== false).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.coupon_rate_pct}% · {s.tenure_months} months · {s.payout_frequency === 'AtMaturity' ? 'at maturity' : 'monthly'}
                </option>
              ))}
            </select>
          </>
        )}
        {/* NCDs are issued in whole ₹1,00,000 units — step/min make the browser
            enforce it. A subordinate bond has no unit rule, so it must not
            carry that step or the browser would block a valid ₹60,000. */}
        {isSob
          ? <input className={sel} placeholder="Amount" type="number" min={1}
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          : <input className={sel} placeholder="Amount (₹1,00,000 units)" type="number" min={LAKH} step={LAKH}
              value={amount} onChange={(e) => setAmount(e.target.value)} />}
        <label className="text-xs flex items-center gap-1.5" title="Date the money was credited to Dhanam's account — interest starts from here once approved">
          Credited<span className="text-danger">*</span> <input className={sel} type="date" value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} />
        </label>
        {/* Same shared list the approval screen uses. Hardcoding it twice is
            how the two screens drift, which is what this list exists to end. */}
        <select className={sel} value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="">Payment method… *</option>
          {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input className={sel} placeholder="Reference / cheque no. *" value={reference} onChange={(e) => setReference(e.target.value)} />
        {/* Which Dhanam account the money was credited to — optional, can be set later. */}
        <select className={sel} value={creditedBank} onChange={(e) => setCreditedBank(e.target.value)} title="Which Dhanam account received the money (optional)">
          <option value="">Credited to… (optional)</option>
          {/* A failed load must not look like "no accounts configured" — that is
              exactly how the 404 above hid for so long. */}
          {collectionBanks.isError && <option value="" disabled>Could not load company accounts</option>}
          {(collectionBanks.data?.rows ?? []).filter((b: any) => b.is_collection_account && b.is_active).map((b: any) => (
            <option key={b.id} value={b.id}>{(b.account_label || b.bank_name)} · {b.account_number}</option>
          ))}
        </select>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer border border-border-strong rounded px-2.5 py-1.5" title="Receipt / cheque photo (image or PDF, under 4 MB)">
          {receipt ? `📎 ${receipt.name.length > 18 ? receipt.name.slice(0, 15) + '…' : receipt.name}` : <>📎 Receipt photo<span className="text-danger">*</span></>}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setReceipt(e.target.files?.[0] ?? null)} />
        </label>
        <label className="text-xs flex items-center gap-1.5" title="Money came from a locker (LockerHub-originated deposits flag themselves automatically)">
          <input type="checkbox" checked={lockerDeposit} onChange={(e) => setLockerDeposit(e.target.checked)} /> Locker deposit
        </label>
        <button disabled={!seriesId || !schemeId || !amount || !(Number(amount) > 0) || create.isPending} onClick={() => {
          const missing = missingRequired();
          if (missing.length) {
            const list = missing.join(', ');
            setErr(`${list.charAt(0).toUpperCase()}${list.slice(1)} ${missing.length > 1 ? 'are' : 'is'} required.`);
            return;
          }
          setErr(''); create.mutate();
        }}
          className="text-xs bg-primary text-white rounded px-4 py-1.5 disabled:opacity-40 hover:bg-primary-hover">
          {clubWith ? 'Add to application' : 'Create investment'}
        </button>
      </div>
      {/* Amber, not red, and it no longer blocks: a part-payment is a normal
          thing to record. It says what happens NEXT, because the risk here is
          not a wrong number — it is a part-payment nobody ever clubs. */}
      {isPartPayment && (
        <div className="text-xs text-warn mt-2">
          Part payment — ₹{Number(amount).toLocaleString('en-IN')} is not a whole ₹1,00,000 unit.
          It will be recorded and held, earning nothing, until it is clubbed up to
          ₹{((Math.floor(Number(amount) / LAKH) + 1) * LAKH).toLocaleString('en-IN')} (or a higher multiple).
          It cannot be approved or go live before then.
        </div>
      )}
      {/* This customer is marked No-TDS, but the investment is over ₹30L — the
          creator must decide whether TDS applies to THIS investment. */}
      {tdsPrompt && (
        <div className="text-xs mt-2 border border-warn/50 bg-surface rounded px-3 py-2">
          <div className="font-medium text-warn">This customer is marked <b>No&nbsp;TDS</b>, but this investment is over ₹30,00,000. Should TDS apply?</div>
          <div className="flex gap-4 mt-1.5">
            <label className="flex items-center gap-1.5"><input type="radio" name="apply-tds" checked={applyTds === 'yes'} onChange={() => setApplyTds('yes')} /> Yes — mark this customer TDS-applicable</label>
            <label className="flex items-center gap-1.5"><input type="radio" name="apply-tds" checked={applyTds === 'no'} onChange={() => setApplyTds('no')} /> No — keep them exempt</label>
          </div>
          {applyTds === 'yes' && <div className="text-text-muted mt-1">The customer will be marked TDS-applicable — 10% is deducted on <b>all</b> their investments from now on, not just this one.</div>}
        </div>
      )}
      {/* Explicitly not offered for a subordinate bond. It would already be
          hidden (the candidates query keys off the series, which a sub bond has
          none of), but relying on that is relying on an accident — and the
          server refuses the combination outright. */}
      {!isSob && clubOptions.length > 0 && (
        <label className="flex items-center gap-2 text-xs text-text-muted mt-3">
          {/* Includes Active investments whose interest hasn't been paid yet
              (owner 2026-08-24) — the added tranche gets its own broken period. */}
          Club into an existing investment:
          <select className={sel} value={clubWith} onChange={(e) => setClubWith(e.target.value)}>
            <option value="">— new application —</option>
            {clubOptions.map((a) => <option key={a.id} value={a.id}>{a.application_no} (₹{Number(a.total_amount).toLocaleString('en-IN')}, {a.status})</option>)}
          </select>
        </label>
      )}
      {err && <div className="text-xs text-danger mt-2">{err}</div>}
      {notice && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 mt-2">{notice}</div>}
    </div>
  );
}

function Field({ label, value }: { label: string; value: unknown }) {
  return (<><dt className="text-text-muted">{label}</dt><dd className="font-medium">{value ? String(value) : '—'}</dd></>);
}

/**
 * Bank accounts: list + add. Name and account number are typed; entering a
 * valid IFSC auto-fills the bank and branch from the directory lookup
 * (/api/lookups/ifsc). Penny-drop verification happens on the server via
 * kycProvider() when the account is added.
 */
function BankAccounts({ customerId, accounts, canEdit, canDelete, onChange, onError }: {
  customerId: number; accounts: any[]; canEdit: boolean; canDelete: boolean; onChange: () => void; onError: (m: string) => void;
}) {
  const { confirm, promptText } = useConfirm();
  const empty = { holder_name: '', account_number: '', ifsc: '', bank_name: '', branch_name: '', branch_city: '' };
  const [f, setF] = useState(empty);
  const [ifscState, setIfscState] = useState<'idle' | 'looking' | 'found' | 'notfound'>('idle');
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const ifscValid = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.ifsc.trim().toUpperCase());

  // Debounced IFSC → bank/branch lookup. Non-blocking: on miss/error the user
  // can still add the account (bank/branch just stay whatever was typed/blank).
  useEffect(() => {
    const code = f.ifsc.trim().toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) { setIfscState('idle'); return; }
    let cancelled = false;
    setIfscState('looking');
    const t = setTimeout(async () => {
      try {
        const r = await api.get<any>(`/api/lookups/ifsc/${code}`);
        if (cancelled) return;
        if (r.found) {
          setF((s) => ({ ...s, bank_name: r.bank, branch_name: r.branch, branch_city: r.city }));
          setIfscState('found');
        } else {
          setF((s) => ({ ...s, bank_name: '', branch_name: '', branch_city: '' }));
          setIfscState('notfound');
        }
      } catch { if (!cancelled) setIfscState('notfound'); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [f.ifsc]);

  // Errors from THIS card are shown INSIDE it. The page-level banner renders up
  // by the profile header, far above the bank section — so a refused delete
  // looked like nothing happened at all.
  const [cardErr, setCardErr] = useState('');
  // Accepts a thrown error OR a plain message, so client-side validation lands
  // in the same inline slot as a server refusal rather than the page banner.
  const fail = (e: unknown) => {
    const m = typeof e === 'string' ? e : e instanceof ApiError ? e.message : 'Failed';
    setCardErr(m); onError(m);
  };

  const add = useMutation({
    mutationFn: () => api.post(`/api/customers/${customerId}/bank-accounts`, {
      ...f, ifsc: f.ifsc.trim().toUpperCase(), holder_name: f.holder_name.trim() || undefined,
      // These are optional server-side, but the zod schema (rightly) rejects an
      // EMPTY string on an .optional() field — only `undefined` is skipped. If
      // the IFSC lookup hasn't resolved yet (or came back notfound), bank_name/
      // branch_name/branch_city sit at '' in state, which used to 400 "Invalid
      // request" on every add attempt whose bank wasn't found or looked up in
      // time — exactly the account-number-typo-fix flow this broke.
      bank_name: f.bank_name.trim() || undefined,
      branch_name: f.branch_name.trim() || undefined,
      branch_city: f.branch_city.trim() || undefined,
    }),
    onSuccess: () => { setF(empty); setIfscState('idle'); onChange(); },
    onError: fail,
  });
  const wrapSet = (p: Promise<unknown>) => p.then(() => { setCardErr(''); onChange(); }).catch(fail);
  const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';

  return (
    <>
      {cardErr && (
        <div className="text-xs text-danger bg-[color:var(--danger-bg)] border border-danger/30 rounded px-3 py-2 mb-3">{cardErr}</div>
      )}
      <div className="divide-y divide-border">
        {accounts.map((b: any) => (
          <div key={b.id} className="py-2.5 flex items-center gap-3 text-sm flex-wrap">
            <div className="min-w-0">
              {b.holder_name && <div className="font-medium truncate">{b.holder_name}</div>}
              <div className="flex items-center gap-2">
                <span className="font-mono">{b.account_number}</span>
                <span className="text-text-muted">{b.ifsc}</span>
              </div>
              {(b.bank_name || b.branch_name) && (
                <div className="text-xs text-text-muted">
                  {[b.bank_name, b.branch_name, b.branch_city].filter(Boolean).join(' · ')}
                </div>
              )}
              {/* Why the penny-drop failed — name mismatch (shows the bank's actual
                  name), a bad account number, or the bank not responding — so a
                  failure is actionable rather than just "Failed". */}
              {b.penny_drop_status !== 'Verified' && b.penny_drop_detail && (
                <div className="text-xs text-danger mt-0.5">{b.penny_drop_detail}</div>
              )}
            </div>
            <span title={b.penny_drop_detail ?? undefined} className={`text-xs rounded px-1.5 py-0.5 ${b.penny_drop_status === 'Verified' ? 'bg-[color:var(--success-bg)] text-success' : b.penny_drop_status === 'Failed' ? 'bg-[color:var(--danger-bg)] text-danger' : 'bg-bg text-text-muted'}`}>{b.penny_drop_status}</span>
            {b.is_active && <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--primary-ring)] text-primary">Active</span>}
            <span className="ml-auto flex items-center gap-3">
              {/* A misspelt beneficiary name is what prints on the bank file —
                  fix it in place rather than re-adding the whole account. */}
              {canEdit && (
                <button onClick={async () => {
                  const next = await promptText({ title: 'Edit beneficiary name', body: 'As it should appear on the bank file.', label: 'Beneficiary name', defaultValue: b.holder_name ?? '', confirmLabel: 'Save' });
                  if (next === null) return;
                  if (next.trim().length < 2) { fail('Beneficiary name is required.'); return; }
                  // Warn BEFORE saving when this will cost the verification, so
                  // the choice is made knowingly rather than discovered after.
                  const key = (v: string) => v.replace(/[^a-z0-9]/gi, '').toUpperCase();
                  if (b.penny_drop_status === 'Verified' && key(next) !== key(b.holder_name ?? '')) {
                    if (!await confirm({
                      title: 'This will clear the verification',
                      body: `The bank confirmed this account under "${b.holder_name ?? ''}". Changing the name to "${next.trim()}" means that confirmation no longer applies, so the account goes back to Pending and has to be verified again.\n\n`
                        + 'The beneficiary name is what goes on the NEFT file.',
                      confirmLabel: 'Change it and re-verify',
                    })) return;
                  }
                  wrapSet(api.patch(`/api/customers/${customerId}/bank-accounts/${b.id}`, { holder_name: next.trim() }));
                }} className="text-xs text-primary hover:underline">Edit name</button>
              )}
              {/* A failed penny-drop must not strand the customer on the wrong
                  account: offer a retry, and allow an explicit override. */}
              {b.penny_drop_status !== 'Verified' && canEdit && (
                <button onClick={() => wrapSet(api.post(`/api/customers/${customerId}/bank-accounts/${b.id}/reverify`))}
                  className="text-xs text-primary hover:underline">Retry verification</button>
              )}
              {!b.is_active && canEdit && (
                <button onClick={async () => {
                  if (b.penny_drop_status === 'Verified') {
                    wrapSet(api.post(`/api/customers/${customerId}/bank-accounts/${b.id}/set-active`));
                    return;
                  }
                  const reason = await promptText({
                    title: `Penny-drop is ${b.penny_drop_status}, not Verified`,
                    body: 'Activate it anyway only if you have confirmed the details another way — future payouts will go here.',
                    label: 'Reason (recorded)', minLength: 3, confirmLabel: 'Activate anyway', danger: true,
                  });
                  if (!reason) return;
                  wrapSet(api.post(`/api/customers/${customerId}/bank-accounts/${b.id}/set-active`, { force: true, reason }));
                }} className="text-xs text-primary hover:underline">Make active</button>
              )}
              {/* Super-admin only. The server refuses while an NCD is pinned to
                  it or unpaid payouts point at it, and says which. */}
              {canDelete && (
                <button
                  onClick={async () => {
                    if (!await confirm({
                      title: `Delete account ${b.account_number}?`,
                      body: `${b.ifsc}. Past payments keep their record; this only removes the account from the customer's file.`,
                      confirmLabel: 'Delete', danger: true,
                    })) return;
                    wrapSet(api.del(`/api/customers/${customerId}/bank-accounts/${b.id}`));
                  }}
                  className="text-xs text-danger hover:underline">Delete</button>
              )}
            </span>
          </div>
        ))}
        {accounts.length === 0 && <div className="py-2 text-text-muted text-sm">No bank accounts yet.</div>}
      </div>

      {canEdit && (
        <div className="mt-3">
          <div className="flex gap-2 items-start flex-wrap">
            <input className={inp} placeholder="Account holder name" value={f.holder_name} onChange={(e) => set('holder_name', e.target.value)} />
            <input className={inp} placeholder="Account number" value={f.account_number} onChange={(e) => set('account_number', e.target.value.replace(/\s/g, ''))} />
            <input className={`${inp} uppercase`} placeholder="IFSC" value={f.ifsc} maxLength={11}
              onChange={(e) => set('ifsc', e.target.value.toUpperCase().replace(/\s/g, ''))} />
            <button disabled={f.account_number.length < 4 || !ifscValid || add.isPending} onClick={() => add.mutate()}
              className="text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40 hover:bg-primary-hover">+ Add &amp; verify</button>
          </div>
          <div className="text-xs mt-1.5 min-h-[1rem]">
            {ifscState === 'looking' && <span className="text-text-muted">Looking up IFSC…</span>}
            {ifscState === 'found' && (
              <span className="text-success">🏦 {[f.bank_name, f.branch_name, f.branch_city].filter(Boolean).join(' · ')}</span>
            )}
            {ifscState === 'notfound' && ifscValid && <span className="text-text-muted">IFSC not found in the directory — you can still add the account.</span>}
          </div>
        </div>
      )}
    </>
  );
}
